import { Amplify } from "aws-amplify";
import { fetchAuthSession } from "aws-amplify/auth";
import { generateClient } from "aws-amplify/data";
import type { Schema } from "../amplify/data/resource";
import { ensureOAuthRedirectStateListener } from "./auth/oauthRedirectState";
import { getRuntimeAmplifyOutputs } from "./auth/oauthRuntimeConfig";

let isConfigured = false;
let client: ReturnType<typeof generateClient<Schema>> | null = null;
let publicIamClient: ReturnType<typeof generateClient<Schema>> | null = null;

type PublicIamGraphqlClient = {
  graphql(input: {
    query: string;
    variables?: Record<string, unknown>;
    authMode: 'iam';
  }): Promise<unknown>;
};

export const ensureAmplifyConfigured = () => {
  if (isConfigured) {
    return;
  }
  ensureOAuthRedirectStateListener();
  Amplify.configure(getRuntimeAmplifyOutputs());
  isConfigured = true;
};

export const getAmplifyClient = () => {
  ensureAmplifyConfigured();
  if (!client) {
    client = generateClient<Schema>({ authMode: "userPool" });
  }
  return client;
};

const getPublicIamAmplifyClient = () => {
  ensureAmplifyConfigured();
  if (!publicIamClient) {
    publicIamClient = generateClient<Schema>({ authMode: 'iam' });
  }
  return publicIamClient;
};

const resolveGraphqlEndpoint = (): string => {
  const endpoint = (getRuntimeAmplifyOutputs() as { data?: { url?: string } }).data?.url;
  if (!endpoint) {
    throw new Error('Amplify outputs are missing the AppSync GraphQL endpoint.');
  }
  return endpoint;
};

const resolveGraphqlApiKey = (): string => {
  const data = (getRuntimeAmplifyOutputs() as {
    data?: {
      api_key?: string;
      apiKey?: string;
      aws_appsync_api_key?: string;
    };
  }).data;
  return String(
    data?.api_key
      ?? data?.apiKey
      ?? data?.aws_appsync_api_key
      ?? '',
  ).trim();
};

const resolveIdToken = async (): Promise<string> => {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) {
    throw new Error('Hosted GraphQL requests require an authenticated Cognito session.');
  }
  return token;
};

export const executeUserPoolGraphqlWithIdToken = async <T>({
  query,
  variables,
}: {
  query: string;
  variables?: Record<string, unknown>;
}): Promise<T> => {
  ensureAmplifyConfigured();
  const response = await fetch(resolveGraphqlEndpoint(), {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      authorization: await resolveIdToken(),
    },
    body: JSON.stringify({ query, variables }),
  });
  const payload = (await response.json()) as T & {
    errors?: Array<{ message?: string }>;
  };
  if (!response.ok) {
    const apiErrors = Array.isArray(payload.errors)
      ? payload.errors.map((entry) => entry.message).filter(Boolean).join('; ')
      : null;
    throw new Error(apiErrors || `AppSync GraphQL request failed with status ${response.status}.`);
  }
  return payload;
};

export const executeApiKeyGraphql = async <T>({
  query,
  variables,
}: {
  query: string;
  variables?: Record<string, unknown>;
}): Promise<T> => {
  ensureAmplifyConfigured();
  const apiKey = resolveGraphqlApiKey();
  if (!apiKey) {
    const publicIamGraphqlClient = getPublicIamAmplifyClient() as unknown as PublicIamGraphqlClient;
    const result = await publicIamGraphqlClient.graphql({
      query,
      variables,
      authMode: 'iam',
    });
    const payload = result as unknown as T & {
      errors?: Array<{ message?: string }>;
    };
    const apiErrors = Array.isArray(payload.errors)
      ? payload.errors.map((entry) => entry.message).filter(Boolean).join('; ')
      : null;
    if (apiErrors) {
      throw new Error(apiErrors);
    }
    return payload;
  }
  const response = await fetch(resolveGraphqlEndpoint(), {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      ...(apiKey ? { 'x-api-key': apiKey } : {}),
    },
    body: JSON.stringify({ query, variables }),
  });
  const payload = (await response.json()) as T & {
    errors?: Array<{ message?: string }>;
  };
  const apiErrors = Array.isArray(payload.errors)
    ? payload.errors.map((entry) => entry.message).filter(Boolean).join('; ')
    : null;
  if (!response.ok || apiErrors) {
    throw new Error(apiErrors || `AppSync public GraphQL request failed with status ${response.status}.`);
  }
  return payload;
};
